// AdminPanel.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import Lookups from './Lookups';
import Users from './Users';
import EmailConfig from './EmailConfig';
import PaymentConfig from './PaymentConfig';
import Documents from './Documents';
import MfaManagement from './MfaManagement';
import ChristmasAdmin from '../../pages/Admin/ChristmasAdmin';
import GlobalAnnouncements from './GlobalAnnouncements';
import WebsiteManagement from './WebsiteManagement';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('lookups');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const tabs = [
    { id: 'lookups', label: 'Lookups' },
    { id: 'users', label: 'Users' },
    { id: 'mfa', label: 'MFA Management' },
    { id: 'emailConfig', label: 'Email Configuration' },
    { id: 'paymentConfig', label: 'Payment Configuration' },
    { id: 'documents', label: 'Documents' },
    { id: 'announcements', label: 'Global Announcements' },
    { id: 'website', label: 'Website Management' },
    { id: 'christmas', label: 'Christmas' }
  ];

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Admin Panel
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          {successMessage && (
            <div className="mt-4 p-4 glass-card border border-green-500/30 text-green-300 rounded-md">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="mt-4 p-4 glass-card border border-red-500/30 text-red-300 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="mt-6 border-b border-white/20">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-blue-400 text-blue-300'
                      : 'border-transparent theme-text-secondary hover:theme-text-primary hover:border-white/30'
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="mt-6 glass-card rounded-lg">
            <div className="p-6">
              {activeTab === 'lookups' && 
                <Lookups 
                  onSuccess={(msg) => setSuccessMessage(msg)} 
                  onError={(msg) => setErrorMessage(msg)} 
                />}
              {activeTab === 'users' && 
                <Users 
                  onSuccess={(msg) => setSuccessMessage(msg)} 
                  onError={(msg) => setErrorMessage(msg)} 
                />}
              {activeTab === 'mfa' && 
                <MfaManagement />}
              {activeTab === 'emailConfig' && 
                <EmailConfig 
                  onSuccess={(msg) => setSuccessMessage(msg)} 
                  onError={(msg) => setErrorMessage(msg)} 
                />}
              {activeTab === 'paymentConfig' && (
                <PaymentConfig 
                  onSuccess={(msg) => setSuccessMessage(msg)}
                  onError={(msg) => setErrorMessage(msg)}
                />
              )}
              {activeTab === 'documents' && 
                <Documents 
                  onSuccess={(msg) => setSuccessMessage(msg)} 
                  onError={(msg) => setErrorMessage(msg)} 
                />}
              {activeTab === 'announcements' && 
                <GlobalAnnouncements />}
              {activeTab === 'website' && 
                <WebsiteManagement />}
              {activeTab === 'christmas' && 
                <ChristmasAdmin />}
            </div>
          </div>
        </div>
      </div>
      </div>
    </ThemeBackground>
  );
};

export default AdminPanel;